import type {
  AtualizarCentroDto,
  CentroAnaliticoResponse,
  CentroArvoreNo,
  CentroCustoResponse,
  CentroNoResponse,
  CentroUsoResponse,
  ConsultaCpmQuery,
  CpmCelulaResponse,
  CpmGradeResponse,
  CriarCentroDto,
  ExcluirCentroResponse,
  GravarCpmDto,
  GravarCpmResponse,
  ListarCentrosQuery,
  MinutosCentroResponse,
} from '@triade/contracts';
import { useConsulta, useMutacao } from './base';

/** Minutos calculados (`['centros','minutos',id]`) mudam com CPM e centro: sao invalidados junto com a arvore e a grade. */
const INVALIDAR = [['centros-custo'], ['centros', 'minutos']];

/** GET /centros-custo: cadastro em ordem natural (centro.ver; PCP ve so o cadastro, sem R$). */
export function useCentros(q: Partial<ListarCentrosQuery> = { limit: 200 }, ativo = true) {
  return useConsulta<CentroCustoResponse[]>(['centros-custo', 'lista'], '/centros-custo', { query: q, ativo });
}

/** GET /centros-custo/arvore: raizes com `filhos` recursivos (sem R$). Inativos so com `incluirInativos`. */
export function useArvore(incluirInativos = false) {
  return useConsulta<CentroArvoreNo[]>(['centros-custo', 'arvore', incluirInativos], '/centros-custo/arvore', {
    query: { incluirInativos: incluirInativos ? 'true' : undefined },
  });
}

/** GET /centros-custo/analiticos: so analiticos, com `caminho` e `rotulo` (combos e a aba Custo por minuto). */
export function useAnaliticos(incluirInativos = false, ativo = true) {
  return useConsulta<CentroAnaliticoResponse[]>(['centros-custo', 'analiticos', incluirInativos], '/centros-custo/analiticos', {
    query: { incluirInativos: incluirInativos ? 'true' : undefined },
    ativo,
  });
}

/** GET /centros-custo/:id: um centro com os campos da arvore. */
export function useCentro(id: string | undefined) {
  return useConsulta<CentroNoResponse>(['centros-custo', 'um', id], `/centros-custo/${id}`, { ativo: !!id });
}

/** GET /centros-custo/:id/uso: o que impede a exclusao definitiva (e se ela e possivel). */
export function useUsoCentro(id: string | undefined, ativo = true) {
  return useConsulta<CentroUsoResponse>(['centros-custo', 'uso', id], `/centros-custo/${id}/uso`, { ativo: !!id && ativo });
}

/** GET /centros-custo/:id/minutos: minutos disponiveis calculados (origem calculado x manual). So para analitico salvo (`id` undefined = inativo). */
export function useMinutosCentro(id: string | undefined) {
  return useConsulta<MinutosCentroResponse>(['centros', 'minutos', id], `/centros-custo/${id}/minutos`, { ativo: !!id });
}

/** GET /centros-custo/cpm: grade centro x mes (so analiticos; sem cpm.ver a API nao manda as celulas). */
export function useGradeCpm(q: Partial<ConsultaCpmQuery>) {
  return useConsulta<CpmGradeResponse>(['centros-custo', 'cpm'], '/centros-custo/cpm', { query: q });
}

/** GET /centros-custo/:id/cpm/:competencia: todas as versoes de um mes (so com cpm.ver). */
export function useVersoesCpm(centroId: string | undefined, competencia: string | undefined) {
  return useConsulta<CpmCelulaResponse[]>(['centros-custo', 'versoes', centroId, competencia], `/centros-custo/${centroId}/cpm/${competencia}`, {
    ativo: !!centroId && !!competencia,
  });
}

export function useCriarCentro() {
  return useMutacao<CentroNoResponse, CriarCentroDto>({ metodo: 'POST', caminho: () => '/centros-custo', corpo: (v) => v, invalidar: INVALIDAR });
}

export function useAtualizarCentro() {
  return useMutacao<CentroNoResponse, { id: string; dto: AtualizarCentroDto }>({
    metodo: 'PATCH',
    caminho: (v) => `/centros-custo/${v.id}`,
    corpo: (v) => v.dto,
    invalidar: INVALIDAR,
  });
}

/** PATCH /centros-custo/:id/mover: novo pai (sintetico) ou null = raiz. */
export function useMoverCentro() {
  return useMutacao<CentroNoResponse, { id: string; parentId: string | null }>({
    metodo: 'PATCH',
    caminho: (v) => `/centros-custo/${v.id}/mover`,
    corpo: (v) => ({ parentId: v.parentId }),
    invalidar: INVALIDAR,
  });
}

/** POST /centros-custo/:id/excluir: exclui de vez, ou desativa e devolve os motivos. */
export function useExcluirCentro() {
  return useMutacao<ExcluirCentroResponse, string>({ metodo: 'POST', caminho: (id) => `/centros-custo/${id}/excluir`, invalidar: INVALIDAR });
}

/** DELETE /centros-custo/:id: desativa (soft delete). */
export function useDesativarCentro() {
  return useMutacao<void, string>({ metodo: 'DELETE', caminho: (id) => `/centros-custo/${id}`, invalidar: INVALIDAR });
}

/** POST /centros-custo/cpm: lote da grade (tudo ou nada); cada alteracao e a proxima versao do mes. */
export function useGravarCpm() {
  return useMutacao<GravarCpmResponse, GravarCpmDto>({ metodo: 'POST', caminho: () => '/centros-custo/cpm', corpo: (v) => v, invalidar: INVALIDAR });
}
